'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Check, Plus } from 'lucide-react'
import { toast } from 'react-toastify'
import type { RecipePayload, ShoppingOverlap } from '@/lib/shoppingLists/types'
import { itemCountLabel } from '@/lib/shoppingLists/catalog'
import { useShoppingLists, type AddRecipeResult, type RecipeRedirect } from './ShoppingListsProvider'
import {
  FieldLabel,
  GreenPillButton,
  ModalCloseButton,
  ShoppingModalShell,
  TextCancelButton,
  TextField,
} from './ShoppingOverlay'

// Where the recipe is going once any "already on another list" question is answered.
type Target = { kind: 'add'; listId: string } | { kind: 'create'; name: string }

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export function AddToShoppingListModal({
  open,
  onClose,
  recipe,
}: {
  open: boolean
  onClose: () => void
  recipe: RecipePayload | null
}) {
  const router = useRouter()
  const { lists, addRecipeToList, createListWithRecipe, getOverlaps } = useShoppingLists()
  const [selectedId, setSelectedId] = useState<string>('')
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)
  // Set while asking whether shared ingredients should go on the other list instead.
  const [overlaps, setOverlaps] = useState<ShoppingOverlap[] | null>(null)
  const [pendingTarget, setPendingTarget] = useState<Target | null>(null)
  const [useOther, setUseOther] = useState<Record<number, boolean>>({})

  const sorted = useMemo(
    () => [...lists].sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt)),
    [lists]
  )

  useEffect(() => {
    if (!open || !recipe) return
    setSelectedId((current) => {
      const stillValid = sorted.some(
        (list) => list.id === current && !list.recipes.some((entry) => entry.id === recipe.id)
      )
      if (stillValid) return current
      const first = sorted.find((list) => !list.recipes.some((entry) => entry.id === recipe.id))
      return first?.id ?? ''
    })
  }, [open, recipe, sorted])

  if (!recipe) return null

  // Confirmation with a way to jump straight to the list.
  const toastAdded = (listId: string, listName: string, result: AddRecipeResult) => {
    const notes = [
      result.combined > 0
        ? `${plural(result.combined, 'ingredient')} combined with items already on the list.`
        : '',
      result.redirected > 0
        ? `${plural(result.redirected, 'ingredient')} added to items on your other lists.`
        : '',
    ].filter(Boolean)
    toast.success(
      <span>
        Added {recipe.name} to {listName}. {notes.join(' ')}{' '}
        <button
          type="button"
          className="font-semibold underline"
          onClick={() => router.push(`/shopping-lists/${listId}`)}
        >
          {result.combined > 0 ? 'Review list' : 'View list'}
        </button>
      </span>
    )
  }

  const reset = () => {
    setCreating(false)
    setNewName('')
    setSelectedId('')
    setOverlaps(null)
    setPendingTarget(null)
    setUseOther({})
    setBusy(false)
    onClose()
  }

  const finish = async (target: Target, redirects: RecipeRedirect[]) => {
    setBusy(true)
    if (target.kind === 'add') {
      const list = lists.find((entry) => entry.id === target.listId)
      if (!list) return setBusy(false)
      const result = await addRecipeToList(list.id, recipe, redirects)
      if (!result.ok) return setBusy(false)
      toastAdded(list.id, list.name, result)
      reset()
      return
    }
    const list = await createListWithRecipe(target.name, recipe, redirects)
    if (!list) return setBusy(false)
    toast.success(`Added ${recipe.name} to ${list.name}`)
    reset()
    router.push(`/shopping-lists/${list.id}`)
  }

  // Ask first if the recipe shares ingredients with items on the person's other lists.
  const proceed = async (target: Target) => {
    setBusy(true)
    const found = await getOverlaps(recipe.id, target.kind === 'add' ? target.listId : undefined)
    if (found.length === 0) return finish(target, [])
    setPendingTarget(target)
    setOverlaps(found)
    // Exact matches are pre-selected; close matches ("flour" vs "King Arthur flour") are not.
    setUseOther(Object.fromEntries(found.map((o) => [o.recipeIngredientId, o.exact])))
    setBusy(false)
  }

  if (overlaps && pendingTarget) {
    const targetName =
      pendingTarget.kind === 'add'
        ? (lists.find((entry) => entry.id === pendingTarget.listId)?.name ?? 'this list')
        : pendingTarget.name
    const chosen = overlaps.filter((o) => useOther[o.recipeIngredientId])
    return (
      <ShoppingModalShell open={open} onClose={reset} widthClass="w-[534px]">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-[#202020]">Already on another list</h2>
            <p className="mt-1 text-base text-[#333333]">
              Add these to the existing item instead of buying them twice. Anything you leave
              unchecked goes on {targetName} as usual.
            </p>
          </div>
          <ModalCloseButton onClick={reset} />
        </div>

        <div className="mt-5 flex max-h-[320px] flex-col gap-2 overflow-y-auto pr-1">
          {overlaps.map((o) => {
            const on = !!useOther[o.recipeIngredientId]
            return (
              <button
                key={o.recipeIngredientId}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setUseOther((current) => ({
                    ...current,
                    [o.recipeIngredientId]: !current[o.recipeIngredientId],
                  }))
                }
                className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                  on ? 'border-[#85C349] bg-[#F3FAEB]' : 'border-[#E6E6E6] bg-white hover:border-[#CBD7DD]'
                }`}
              >
                <span
                  className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[3px] border ${
                    on ? 'border-[#85C349] bg-[#85C349] text-white' : 'border-[#A6BACC] bg-white'
                  }`}
                >
                  {on ? <Check className="h-3 w-3" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base font-semibold text-[#202020]">
                    {o.ingredientName}
                  </span>
                  <span className="block break-words text-[13px] text-[#6B6B6B]">
                    {o.exact ? 'Already' : 'Looks like'}: {o.itemName}
                    {o.itemQuantity ? ` (${o.itemQuantity})` : ''} on {o.listName}
                  </span>
                </span>
                {!o.exact ? (
                  <span className="shrink-0 rounded-full border border-[#CBD7DD] bg-[#F7F7F7] px-2.5 py-1 text-xs font-semibold text-[#6B6B6B]">
                    Close match
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>

        {chosen.length > 0 ? (
          <p className="mt-4 text-sm text-[#333333]">
            {plural(chosen.length, 'ingredient')} will be added to existing items on your other
            lists.
          </p>
        ) : null}
        <div className="mt-4 flex items-center justify-end gap-7">
          <TextCancelButton
            onClick={() => {
              setOverlaps(null)
              setPendingTarget(null)
            }}
          />
          <GreenPillButton
            disabled={busy}
            onClick={() =>
              finish(
                pendingTarget,
                chosen.map((o) => ({ recipeIngredientId: o.recipeIngredientId, itemId: o.itemId }))
              )
            }
          >
            Add recipe
          </GreenPillButton>
        </div>
      </ShoppingModalShell>
    )
  }

  if (creating) {
    return (
      <ShoppingModalShell open={open} onClose={reset}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-[#202020]">
              Create a new shopping list
            </h2>
            <p className="mt-1 text-base text-[#333333]">
              {recipe.name} will be added automatically to this list.
            </p>
          </div>
          <ModalCloseButton onClick={reset} />
        </div>
        <div className="mt-6 flex flex-col gap-2">
          <FieldLabel>Shopping List Name</FieldLabel>
          <TextField
            value={newName}
            onChange={setNewName}
            placeholder="e.g. Weekly Grocery"
            autoFocus
          />
        </div>
        <div className="mt-6 flex items-center justify-end gap-7">
          <TextCancelButton onClick={() => setCreating(false)} />
          <GreenPillButton
            disabled={!newName.trim() || busy}
            onClick={() => proceed({ kind: 'create', name: newName.trim() })}
          >
            Create & Add Recipe
          </GreenPillButton>
        </div>
      </ShoppingModalShell>
    )
  }

  return (
    <ShoppingModalShell open={open} onClose={reset} widthClass="w-[534px]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#202020]">Add to shopping list</h2>
          <p className="mt-1 text-base text-[#333333]">
            Every ingredient in {recipe.name} is added and sorted by department.
          </p>
        </div>
        <ModalCloseButton onClick={reset} />
      </div>

      <div className="mt-5 flex max-h-[320px] flex-col gap-2 overflow-y-auto pr-1">
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="flex w-full items-center gap-3 rounded-xl border border-dashed border-[#85C349] bg-white p-3 text-left text-base font-semibold text-[#3D7A1C] transition-colors hover:bg-[#F3FAEB]"
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#85C349] text-white">
            <Plus className="h-3 w-3" />
          </span>
          Create a new list
        </button>
        {sorted.map((list) => {
          const selected = selectedId === list.id
          const added = list.recipes.some((entry) => entry.id === recipe.id)
          return (
            <button
              key={list.id}
              type="button"
              disabled={added}
              onClick={() => setSelectedId(list.id)}
              aria-pressed={selected}
              className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                added
                  ? 'cursor-not-allowed border-[#E6E6E6] bg-[#F7F7F7]'
                  : selected
                    ? 'border-[#85C349] bg-[#F3FAEB]'
                    : 'border-[#E6E6E6] bg-white hover:border-[#CBD7DD]'
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                  selected
                    ? 'border-[#85C349] bg-[#85C349] text-white'
                    : 'border-[#A6BACC] bg-white'
                }`}
              >
                {selected ? <Check className="h-3 w-3" /> : null}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block truncate text-base font-semibold ${
                    added ? 'text-[#6B6B6B]' : 'text-[#202020]'
                  }`}
                >
                  {list.name}
                </span>
                <span className="block text-[13px] text-[#6B6B6B]">
                  {itemCountLabel(list.items.length)} · {list.recipes.length} recipe
                  {list.recipes.length === 1 ? '' : 's'}
                </span>
              </span>
              {added ? (
                <span className="shrink-0 rounded-full border border-[#85C349] bg-[#F3FAEB] px-2.5 py-1 text-xs font-semibold text-[#3D7A1C]">
                  Already added
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      <div className="mt-6 flex items-center justify-end gap-7">
        <TextCancelButton onClick={reset} />
        <GreenPillButton
          disabled={!selectedId || busy}
          onClick={() => proceed({ kind: 'add', listId: selectedId })}
        >
          Add to List
        </GreenPillButton>
      </div>
    </ShoppingModalShell>
  )
}
